import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

// components
import Container from 'landing-page/components/common/Container';
import SectionHeader from 'landing-page/components/home/SectionHeader';
import { AlumniPlaceholder } from './Placeholder';

//hooks
import { api, AlumniType } from 'landing-page/helpers/api';
import { useApi } from 'landing-page/hooks/useApi';

// styles
import './style.css';

interface AlumniProps {
    autoFetch?: boolean;
    items?: AlumniType[]
}

const Alumni: React.FC<AlumniProps> = ({
    autoFetch = false,
    items: propItems = []
}) => {

    const navigate = useNavigate();

    const { data, loading, error, execute: fetchAlumniData } = useApi(api.getStarredAlumni.bind(api));

    const [alumniItems, setAlumniItems] = useState<AlumniType[]>(propItems);

    const [currentIndex, setCurrentIndex] = useState(0);

    const [isTransitioning, setIsTransitioning] = useState(false);
    
    const carouselRef = useRef<HTMLDivElement>(null);
    
    const [visibleCards, setVisibleCards] = useState(5);

    useEffect(() => {
        if (autoFetch && propItems.length === 0) {
            fetchAlumniData();
        } else if (propItems.length > 0) {
            setAlumniItems(propItems);
        }
    }, [autoFetch, propItems.length]);

    useEffect(() => {
        if (data?.data) {
            setAlumniItems(data.data);
        }
    }, [data]);

    // Responsive breakpoints
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 640) {
                setVisibleCards(1);
            } else if (window.innerWidth < 769) {
                setVisibleCards(2);
            } else if (window.innerWidth < 1024) {
                setVisibleCards(3);
            } else if (window.innerWidth < 1280) {
                setVisibleCards(4);
            } else {
                setVisibleCards(4); // Changed to 4 cards for large screens
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset current index when visible cards change to prevent overflow
    useEffect(() => {
        let newMaxIndex = 0;
        newMaxIndex = Math.max(0, alumniItems.length - visibleCards);
        if (currentIndex > newMaxIndex) {
            setCurrentIndex(newMaxIndex);
        }
    }, [visibleCards, currentIndex, alumniItems.length]);

    // Calculate the maximum index that can be scrolled to
    // This ensures we can scroll to show the last item fully
    const maxIndex = Math.max(0, alumniItems.length - visibleCards);

    // Check if we can go to the next slide
    const canGoNext = currentIndex < maxIndex;
    
    // Check if we can go to the previous slide
    const canGoPrev = currentIndex > 0;

    // Check if we're at the last possible position
    const isAtLastPosition = currentIndex === maxIndex;

    const nextSlide = useCallback(() => {
        if (canGoNext && !isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
            setTimeout(() => setIsTransitioning(false), 300);
        }
    }, [canGoNext, maxIndex, isTransitioning]);

    const prevSlide = useCallback(() => {
        if (canGoPrev && !isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(prev => Math.max(prev - 1, 0));
            setTimeout(() => setIsTransitioning(false), 300);
        }
    }, [canGoPrev, isTransitioning]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                prevSlide();
            } else if (e.key === 'ArrowRight') {
                nextSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [prevSlide, nextSlide]);

    // Auto-play functionality - Fixed to show all data
    useEffect(() => {
        const interval = setInterval(() => {
            if (isAtLastPosition) {
                // If we're at the last position, reset to beginning
                setCurrentIndex(0);
            } else {
                // Otherwise, move to next position
                setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [isAtLastPosition, maxIndex]);

    const handleViewStory = (alumni: AlumniType) => {
        // Navigate to alumni story page
        // window.location.href = alumni.storyUrl || '#';

        navigate(`/flip-book/${alumni.id}`);
    };

    const handleExploreMore = () => {
        // Navigate to alumni directory
        // window.location.href = '/alumni';
    };

    // Calculate the number of progress indicators needed
    const progressCount = Math.max(1, maxIndex + 1);

    // Calculate the transform distance based on card width and gap
    const getTransformDistance = () => {
        // Simple calculation: each slide moves by one card width + gap
        const slideWidthPercentage = 100 / visibleCards;
        return currentIndex * slideWidthPercentage;
    };

    return (
        <Container>
            <SectionHeader
                autoFetch={false}
                dataType="header_6"
                withTitleAnimation={true}
                withDescriptionAnimation={false}
                title="Featured Alumni"
                subtitle="Kisah alumni kami yang berdampak nyata di dunia. Dari inovator hingga pemimpin sosial"
            />

            { loading && autoFetch && <AlumniPlaceholder /> }

            { autoFetch && (!data?.data || data.data.length === 0) && !loading && <AlumniPlaceholder /> }
            
            { autoFetch && data?.data && data.data.length > 0 && !loading && (
                <>
                    {/* Alumni Carousel */}
                    <div className="position-relative">
                        {/* Carousel Container */}
                        <div 
                            ref={carouselRef}
                            className="alumni-carousel-container"
                        >
                            {/* Cards Container */}
                            <div 
                                className="alumni-cards-wrapper"
                                style={{
                                    transform: `translateX(-${getTransformDistance()}%)`
                                }}
                            >
                                {alumniItems.map(alumni => (
                                    <div key={alumni.id} className="alumni-card-item">
                                        <AlumniCard 
                                            alumni={alumni} 
                                            onViewStory={handleViewStory}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Navigation Arrows */}
                        {canGoPrev && (
                            <button
                                onClick={prevSlide}
                                disabled={isTransitioning}
                                className="alumni-nav-button prev"
                                aria-label="Previous alumni"
                            >
                                <i className="fas fa-chevron-left text-dark"></i>
                            </button>
                        )}

                        {canGoNext && (
                            <button
                                onClick={nextSlide}
                                disabled={isTransitioning}
                                className="alumni-nav-button next"
                                aria-label="Next alumni"
                            >
                                <i className="fas fa-chevron-right text-dark"></i>
                            </button>
                        )}

                        {/* Progress Indicators */}
                        {progressCount > 1 && (
                            <div className="alumni-progress-indicators">
                                {Array.from({ length: progressCount }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setIsTransitioning(true);
                                            setCurrentIndex(i);
                                            setTimeout(() => setIsTransitioning(false), 300);
                                        }}
                                        className={`alumni-progress-dot ${i === currentIndex ? 'active' : ''}`}
                                        aria-label={`Go to slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Call to Action Button */}
                    <div className="text-center mt-10">
                        <Link to="/alumni" className="alumni-cta-button">
                            JELAJAHI ALUMNI LAINNYA
                        </Link>
                    </div>
                </>
            )}
        </Container>
    );
};

// Alumni Card Component
interface AlumniCardProps {
    alumni: AlumniType;
    onViewStory: (alumni: AlumniType) => void;
}

const AlumniCard: React.FC<AlumniCardProps> = ({ alumni, onViewStory }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const getHeaderClass = (degree: string | null) => {
        if (!degree) return 'master';

        if (degree) {
            const value = degree.toLowerCase();
            return value === 'DOKTOR' ? 'doctor' : 'master';
        }

        return 'master';
    };

    return (
        <div className="alumni-card">
            {/* Header Section */}
            <div className={`alumni-card-header ${getHeaderClass(alumni.jenjang_studi)}`}>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <span className="alumni-degree-tag">
                            {alumni.jenjang_studi == '' || alumni.jenjang_studi == null ? '-' : alumni.jenjang_studi}
                        </span>
                        <span className="alumni-degree-tag">
                            {alumni.batch_pk}
                        </span>
                    </div>
                    <div className="alumni-header-icon">
                        <i className="fas fa-leaf text-white"></i>
                    </div>
                </div>
                
                {/* Name and Position */}
                <div className="mt-3">
                    <h3 className="alumni-name">
                        {alumni.nama_lengkap}
                    </h3>
                    <p className="alumni-position">
                        {alumni.pekerjaan_saat_ini} {alumni.perusahaan_instansi}
                    </p>
                </div>
            </div>

            {/* Image Section */}
            <div className="alumni-image-container">
                {!imageError ? (
                    <img
                        src={alumni.foto_profil_url || ''}
                        alt={alumni.nama_lengkap}
                        className={`alumni-image ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="alumni-image-placeholder">
                        <div>
                            <i className="fas fa-user fa-3x mb-2"></i>
                            <p className="small">Photo unavailable</p>
                        </div>
                    </div>
                )}
                
                {/* Loading State */}
                {!imageLoaded && !imageError && (
                    <div className="alumni-loading">
                        <i className="fas fa-spinner fa-spin fa-2x text-warning"></i>
                    </div>
                )}

                {/* View Story Button */}
                <button
                    onClick={() => onViewStory(alumni)}
                    className="alumni-view-story-btn"
                >
                    <span>Lihat Cerita</span>
                    <i className="fas fa-chevron-right"></i>
                </button>
            </div>
        </div>
    );
};

export { Alumni, AlumniCard };